/**
 * 検証タブコンポーネント
 *
 * 取引先設定を使ったOCR→CSV生成→メール設定確認の全体フローをテスト
 */

import { useState, useCallback, useEffect } from "react";
import {
  Stack,
  Title,
  Text,
  Group,
  Button,
  Card,
  Alert,
  Box,
  Loader,
} from "@mantine/core";
import { showSuccess, showError, showWarning } from "../utils/notifications";
import { Play, Scan } from "lucide-react";
import {
  runPlayground,
  PlaygroundResult,
} from "../services/playgroundService";
import { PlaygroundFileUploader } from "./playground/PlaygroundFileUploader";
import { PlaygroundTimeline } from "./playground/PlaygroundTimeline";

/**
 * プロパティ型
 */
interface TestPlaygroundTabProps {
  customerId: string;
  partnerId: string;
  partnerName?: string;
}

/**
 * 検証タブコンポーネント
 */
export function TestPlaygroundTab({
  customerId,
  partnerId,
  partnerName = "取引先",
}: TestPlaygroundTabProps) {
  // ファイル状態
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // 処理状態
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<PlaygroundResult | null>(null);

  // 取引先が変わったときに状態をリセット
  useEffect(() => {
    setResult(null);
    setSelectedFile(null);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, [partnerId]);

  /**
   * ファイル選択を処理
   */
  const handleDrop = useCallback((files: File[]) => {
    if (files.length > 0) {
      const file = files[0];
      setSelectedFile(file);
      setResult(null); // 新しいファイルを選択したら結果をクリア
      // プレビューURLを生成
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  }, []);

  /**
   * ファイルを削除
   */
  const handleRemoveFile = useCallback(() => {
    // プレビューURLをクリーンアップ
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setSelectedFile(null);
    setResult(null);
  }, [previewUrl]);

  /**
   * 検証を実行
   */
  const handleRunTest = useCallback(async () => {
    if (!selectedFile) {
      showError("ファイルを選択してください", "検証エラー");
      return;
    }

    setIsProcessing(true);
    setResult(null);

    try {
      // ファイルをBase64に変換
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = (reader.result as string).split(",")[1];

        try {
          const data = await runPlayground({
            customerId,
            partnerId,
            fileBase64: base64,
            mimeType: selectedFile.type,
            fileName: selectedFile.name,
          });

          setResult(data);

          if (data.success) {
            showSuccess("全てのステップが正常に完了しました", "検証完了");
          } else {
            showWarning("いくつかのステップでエラーが発生しました", "検証完了（一部エラー）");
          }
        } catch (error) {
          const message =
            error instanceof Error ? error.message : String(error);
          showError(message, "検証エラー");
        } finally {
          setIsProcessing(false);
        }
      };

      reader.onerror = () => {
        showError("ファイルの読み込みに失敗しました", "ファイルエラー");
        setIsProcessing(false);
      };

      reader.readAsDataURL(selectedFile);
    } catch {
      setIsProcessing(false);
    }
  }, [selectedFile, customerId, partnerId]);

  /**
   * CSVをダウンロード
   */
  const handleDownloadCsv = useCallback(() => {
    if (!result?.csvPreview) return;

    const blob = new Blob([result.csvPreview.content], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = result.csvPreview.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [result]);

  return (
    <Stack gap="lg">
      {/* ヘッダー */}
      <Box>
        <Group gap="xs" mb="xs">
          <Scan size={20} />
          <Title order={4}>検証</Title>
        </Group>
        <Alert
          icon={<Scan size={16} />}
          title="検証機能について"
          color="blue"
          variant="light"
          radius="md"
        >
          <Text size="xs" mb="sm">
            「{partnerName}
            」の設定を使って、OCR→CSV生成→メール設定確認の全体フローをテストします。本番データには影響しません。
          </Text>

          <Text size="xs" fw={700} mb={4}>
            検証の流れ
          </Text>
          <Box component="ul" style={{ margin: 0, paddingLeft: "1.2rem" }}>
            <Text component="li" size="xs">
              <strong>1. ファイルアップロード:</strong>{" "}
              テスト用の伝票画像（PDF/JPEG/PNG/TIFF、最大10MB）をアップロードします。
            </Text>
            <Text component="li" size="xs" mt={4}>
              <strong>2. 検証実行:</strong>{" "}
              AIによるOCR解析→CSVへの変換→メール設定の確認を一括で行います。
            </Text>
            <Text component="li" size="xs" mt={4}>
              <strong>3. 結果確認:</strong>{" "}
              各ステップの結果を確認し、設定の調整に役立てます。
            </Text>
          </Box>
        </Alert>
      </Box>

      {/* ファイルアップロード */}
      <PlaygroundFileUploader
        selectedFile={selectedFile}
        previewUrl={previewUrl}
        isProcessing={isProcessing}
        onDrop={handleDrop}
        onRemoveFile={handleRemoveFile}
      />

      {/* 実行ボタン */}
      <Card withBorder>
        <Stack gap="md">
          <Title order={5}>2. 検証を実行</Title>
          <Button
            size="lg"
            leftSection={
              isProcessing ? (
                <Loader size={20} color="white" />
              ) : (
                <Play size={20} />
              )
            }
            onClick={handleRunTest}
            disabled={!selectedFile || isProcessing}
            fullWidth
          >
            {isProcessing ? "処理中..." : "検証を開始"}
          </Button>
        </Stack>
      </Card>

      {/* 結果表示 */}
      {result && (
        <PlaygroundTimeline result={result} onDownloadCsv={handleDownloadCsv} />
      )}
    </Stack>
  );
}
